import { AccountantService, ClientService } from "../../services";
import { AppBar, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { GET_QUERY_KEY, QUERY_OPTIONS } from "../../common/constants";
import { IResponse, IServerError } from "../../models/common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../store/actions";
import {
	sortDDLByLabel,
	useNavigateToLogout,
	useRemoveQueriesOnUnmount,
} from "../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CRACreditService } from "../../services/CRACreditService";
import { CompanyTD1FormService } from "../../services/CompanyTD1FormService";
import { EmployeeService } from "../../services/EmployeeService";
import FileSaver from "file-saver";
import { IAppState } from "../../store/reducers";
import { IDropDownListResponse } from "../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../models";
import { LookupFieldService } from "../../services/LookupFieldService";
import { ROEService } from "../../services/ROEService";
import { ReportsService } from "../../services/ReportsService";
import { WebROESubmissionDataGridWithColumnToggled } from "./WebROESubmissionDataGridWithColumnToggled";
import { WebROESubmissionHeader } from "./WebROESubmissionHeader";
import { WebROESubmissionToolbar } from "./WebROESubmissionToolbar/WebROESubmissionToolbar";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { useNavigate } from "react-router-dom";

export interface IEDRReportSetupPage { }
const WEB_ROE_SUBMISSION_QUERY_KEY = "web-roe-submission";
const CLIENT_DDL_DATA_QUERY_KEY = "client-ddl-data-record";
const ACCOUNTANT_DDL_DATA_QUERY_KEY = "accountant-ddl-data-record";
const EMPLOYEE_DDL_DATA_QUERY_KEY = "employee-ddl-data-record"

const useWebROESubmissionData = (
	runGetListAPICall: boolean,
	accountingProfessional: string,
	showEmployees: string,
	company: string,
	accountType: string,
	showArchive: boolean,
	params: IGetListRequest,
) =>
	useQuery({
		queryKey: [
			WEB_ROE_SUBMISSION_QUERY_KEY,
			runGetListAPICall,
			accountingProfessional,
			showEmployees,
			company,
			accountType,
			showArchive,
			params,
		],
		queryFn: () =>
			ROEService.getWebROESubmissionData(
				runGetListAPICall,
				accountingProfessional,
				showEmployees,
				company,
				accountType,
				showArchive,
				params
			),
		refetchOnWindowFocus: false,
	});

const useGetEmployeeDDL = (clientId: string) =>
	useQuery({
		queryKey: [EMPLOYEE_DDL_DATA_QUERY_KEY, clientId],
		queryFn: () => {
			return EmployeeService.getEmployeeDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});

const useGetAccountantDDLItemsItems = () =>
	useQuery({
		queryKey: [ACCOUNTANT_DDL_DATA_QUERY_KEY],
		queryFn: (data) => {
			return AccountantService.getGetAccountantDDL();
		},
		refetchOnWindowFocus: false,
	});
const useGetClientsDDLItems = (accountantId: string) =>
	useQuery({
		queryKey: [CLIENT_DDL_DATA_QUERY_KEY, accountantId],
		queryFn: (data) => {
			return ClientService.getGetClientDDL(accountantId);
		},
		refetchOnWindowFocus: false,
	});


export const WebROESubmission: React.FC<IEDRReportSetupPage> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoading, setIsLoading] = useState(false);
	const [webROESubmissionData, setWebROESubmissionData] =
		useState<any>(undefined);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);
	// const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
	// const [employeeStatus, setEmployeeStatus] = useState<number>(100);
	const [accountantId, setAccountantId] = useState("all");
	const [selectedClientId, setSelectedClientId] = useState(clientId);
	const [appliedShowEmployees, setAppliedShowEmployees] = useState<number>(300);
	const [appliedShowArchive, setAppliedShowArchive] = useState<boolean>(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [openDialog, setDialogOpen] = useState(false);

	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const accountingProfessional = formMethods.getValues("accountingProfessional");
	const showEmployees = formMethods.getValues("showEmployees");
	const company = formMethods.getValues("company");
	const accountType = formMethods.getValues("accountType");
	const showArchive = formMethods.getValues("showArchive");

	const { isPending, data } = useWebROESubmissionData(
		runGetListAPICall,
		accountingProfessional,
		showEmployees,
		company,
		accountType,
		showArchive,
		queryParams,
	);
	const webROESubmissionList: any | null = data;

	useEffect(() => {
		if (webROESubmissionList) {
			setWebROESubmissionData(webROESubmissionList);
		}
	}, [webROESubmissionList]);

	const { isPending: isPendingAccountantDDLItemsItems, data: accountantDDL } =
		useGetAccountantDDLItemsItems();
	const { isPending: isPendingClientsDDLItems, data: clientsDDL } =
		useGetClientsDDLItems(accountantId);
	const { data: EmployeeDDL } = useGetEmployeeDDL(selectedClientId);

	const webROESubmissionDataAPICall = (
		accountingProfessional: string,
		showEmployees: string,
		company: string,
		accountType: string,
		showArchive: boolean
	) => {
		const fetchData = async () => {
			setIsLoading(true);

			try {
				const response: any = await ROEService.getWebROESubmissionData(
					true,
					accountingProfessional,
					showEmployees,
					company,
					accountType,
					showArchive,
					queryParams,

				);

				if (response) {
					setWebROESubmissionData(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {
		setAppliedShowEmployees(values.showEmployees ?? 300);
		setAppliedShowArchive(values.showArchive ?? false);
		values.webProcessingMethod,
			values.fileName
		webROESubmissionDataAPICall(
			values.accountingProfessional,
			values.showEmployees,
			values.company,
			values.accountType,
			values.showArchive
		);


	};

	const [zeroEmployee, setzeroEmployee] = useState(true);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);

	const {
		isPending: isSubmittingAddRemoveHeaderColumns,
		mutate: submitAddRemoveHeaderColumns,
	} = useMutation({
		mutationFn: ReportsService.getEDRAddRemoveHeaderColumns,
		onSuccess: (res: any | IServerError | IResponse) => {
			if (res.status === 200) {
				setRunGetListAPICall(true)
				return queryClient.invalidateQueries({
					queryKey: [WEB_ROE_SUBMISSION_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message
						? res.message
						: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err: any) => {
			setSnackbarData({
				open: true,
				message: err.message,
				severity: "error",
			});
		},
	});


	useEffect(() => {
		if (submitAddRemoveColumnsHandler) {
			submitAddRemoveHeaderColumns({
				clientId: clientId,
				columns: submitAddRemoveColumnsHandler,
				navigateToLogout: navigateToLogout,
			});
		}
	}, [submitAddRemoveColumnsHandler]);


	const accountantsDDLItems = useMemo(() => {
		if (accountantDDL) {
			if (
				accountantDDL &&
				Array.isArray(accountantDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(accountantDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [accountantDDL]);

	const clientsDDLItems = useMemo(() => {
		if (clientsDDL) {
			if (
				clientsDDL &&
				Array.isArray(clientsDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(clientsDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [clientsDDL]);


	// const { isPending: isDeleting, mutate: deleteWebROESubmissionById } = useMutation({
	// mutationFn: ROEService.deleteById, // add later here delete Api
	// 		onSuccess: (res: string | IServerError | IResponse | any) => {
	// 			if (
	// 				typeof res === "object" &&
	// 				res?.message === "Accountant has been deleted successfully"
	// 			) {
	// 				
	// 				queryClient.invalidateQueries(WEB_ROE_SUBMISSION_QUERY_KEY);
	// 				setSnackbarData({
	// 					open: true,
	// 					message: "Success",
	// 					severity: "success",
	// 				});
	// 				formMethods.reset();
	// 				let path = `/web-roe`;
	// 				navigate(path);
	// 			} else {
	// 				setSnackbarData({
	// 					open: true,
	// 					message: "Something went wrong, try again later!",
	// 					severity: "error",
	// 				});
	// 			}
	// 		},
	// 		onError: () => {
	// 			// TODO: customize the toast message based on the response
	// 			setSnackbarData({
	// 				open: true,
	// 				message: "Something went wrong, try again later!",
	// 				severity: "error",
	// 			});
	// 		},
	// 	});


	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useRemoveQueriesOnUnmount(["employee-report"]);

	const updatedWebROESubmissionListItems = useMemo(() => {
		return (
			webROESubmissionData &&
			webROESubmissionData.items &&
			webROESubmissionData?.items.map((item: any) => ({
				...item,
			}))
		);
	}, [webROESubmissionData]);



	// const deleteAccountantField = () => {
	// 	if (singleDelete) {
	// 		setSingleDelete(false);
	// 		deleteWebROESubmissionById();
	// 		setDialogOpen(false);
	// 	}
	// };

	// const onCloseHandler = () => {
	// 	setDialogOpen(false);
	// };

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		// if (selectedDataRecord) {
		// 	setEmployeeId(selectedDataRecord.id); // change later
		// }
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
	};

	const onAccountantsSelectionChangeHandler = (event: any) => {
		setRunGetListAPICall(false)
		setAccountantId(event.value)
		formMethods.setValue("client", undefined);
		// formMethods.setValue("employee", undefined);
	}

	const onClientsSelectionChangeHandler = (event: any) => {
		setRunGetListAPICall(false);
		setSelectedClientId(event.value);
		// formMethods.setValue("employee", undefined);
	};

	const onChangeFileAcceptedHandler = () => {
		alert("file-accepted")
	};

	const onChangeFileRejectedHandler = () => {
		alert("file-rejected")
	};
	const onChangeArchiveHandler = () => {
		alert("archive")
	};

	const onChangeRemoveArchiveHandler = () => {
		alert("remove-archive")
	};

	const expandedLabels = ["Misc.", "Web ROE Submission "];
	const expandedwebROESubmissionOrSetupItem = "Web ROE Submission";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Misc." &&
				item.label === expandedwebROESubmissionOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedwebROESubmissionOrSetupItem && "/web-roe",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);


	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_employee_list.png";

	return (
		<>
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Web ROE Submission"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<WebROESubmissionHeader
						// onApplyButtonClickedHandler={onApplyButtonClickedHandler}
						acountingProfessionalDDLItems={accountantsDDLItems}
						companyDDLItems={clientsDDLItems}
						onAccountantsSelectionChangeHandler={onAccountantsSelectionChangeHandler}
						onClientsSelectionChangeHandler={onClientsSelectionChangeHandler}
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
					/>
				</form>


				<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
					<BackdropCircularProgress
						open={
							// isPending ||
							isSubmittingAddRemoveHeaderColumns ||
							isLoading ||
							isPending ||
							isPendingAccountantDDLItemsItems ||
							isPendingClientsDDLItems
							// || isDeleting
						}
						testId="web-roe-submission"
					/>

					{/* <DeleteConfirmationDialog
									open={openDialog}
									deleteActionHandler={deleteAccountantField}
									cancelActionHandler={onCloseHandler}
									testId="accountant-confirmation-dialog"
								/> */}

					<WebROESubmissionDataGridWithColumnToggled
						columns={
							webROESubmissionData?.headerColumns
								? webROESubmissionData.headerColumns.filter(
									(item: any) => item.field !== "id"
								)
								: []
						}
						completeHeaderColumnsList={
							// webROESubmissionData
							// 	&& webROESubmissionData
							// 		?.totalHeaderColumns &&
							// 	Object.keys(webROESubmissionData.totalHeaderColumns).length !== 0 /// comment for use hardcoded data change later
							// 	? webROESubmissionData.totalHeaderColumns
							// 	: {}
							webROESubmissionData?.headerColumns ?? []
						}
						submitAddRemoveColumnsHandler={(value: any) =>
							setSubmitAddRemoveColumnsHandler(value)
						}
						onColumnReorder={(value: any) => setOnColumnReorder(value)}
						rows={
							// webROESubmissionData && updatedWebROESubmissionListItems
							// 	? updatedWebROESubmissionListItems
							// 	: []
							webROESubmissionData?.items ?? []
							// itams
						}
						showIndexColumn={true}
						showChecboxColumn={true}
						showSettingColumn={false}
						onSnackbarMessageData={(value: any) => setSnackbarData(value)}
						totalRecords={
							webROESubmissionData && webROESubmissionData.total
								? webROESubmissionData.total
								: 0
						}
						initialPageSize={INITIAL_PAGE_SIZE}
						rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
						onRequestParamsChange={onRequestParamsChange}
						searchFieldPlaceholder={"Search..."}
						changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
							setzeroEmployee(value)
						}
						pageTitle={"Web ROE Submisson"}
					/>

					{webROESubmissionData ? (
						<AppBar
							position="sticky"
							color="default"
							elevation={1}
							sx={{
								backgroundColor: "#F7F7F7",

								boxShadow: "none",
								alignItems: "center",
								zIndex: 0,
								position: "sticky",
								bottom: 0,
							}}
						>
							<WebROESubmissionToolbar
								onResetClicked={onResetClicked}
								onDeleteClicked={onDeleteClicked}
								appliedShowEmployees={appliedShowEmployees}
								appliedShowArchive={appliedShowArchive}
								onChangeFileAcceptedHandler={onChangeFileAcceptedHandler}
								onChangeFileRejectedHandler={onChangeFileRejectedHandler}
								onChangeArchiveHandler={onChangeArchiveHandler}
								onChangeRemoveArchiveHandler={onChangeRemoveArchiveHandler}
							// isSubmitting={isSubmitting}
							// id={id}
							/>
						</AppBar>
					) : (
						<></>
					)}
				</Grid>
			</FormProvider>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
